import React, { useState } from 'react';
import { Mail, Bell, Clock, FileText } from 'lucide-react';

const NotificationTab = ({ isDarkMode }) => {
  const [marketingUpdates, setMarketingUpdates] = useState(false);
  const [chatMessages, setChatMessages] = useState(true);
  const [digestFrequency, setDigestFrequency] = useState('Daily');
  const [quietHours, setQuietHours] = useState({ from: '22:00', to: '07:00' });
  const [researchDocuments, setResearchDocuments] = useState(false);

  return (
    <>
      <div className="flex flex-col gap-4 px-2">
        {/* Email Notifications */}
        <div
          className={`border rounded-lg p-6 shadow-sm space-y-4 ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-200 text-gray-800'
          }`}
        >
          <div className="flex items-start">
            <div
              className={`p-2 rounded-md mr-3 flex items-center justify-center ${
                isDarkMode
                  ? 'bg-white/10 text-white'
                  : 'bg-indigo-100 text-indigo-500'
              }`}
            >
              <Mail className="w-4 h-4" />
            </div>
            <div>
              <h3
                className={`font-semibold text-sm mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Email Notifications
              </h3>
              <p
                className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Control which email notifications you receive
              </p>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Marketing Updates
              </p>
              <p
                className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Receive news about product updates and features
              </p>
            </div>
            <button
              onClick={() => setMarketingUpdates(!marketingUpdates)}
              className={`relative inline-flex items-center h-5 w-10 rounded-full transition-colors ${
                marketingUpdates ? 'bg-brand' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform bg-white rounded-full transition ${
                  marketingUpdates ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Push Notifications */}
        <div
          className={`border rounded-lg p-6 shadow-sm space-y-4 ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-200 text-gray-800'
          }`}
        >
          <div className="flex items-start">
            <div
              className={`p-2 rounded-md mr-3 flex items-center justify-center ${
                isDarkMode
                  ? 'bg-white/10 text-white'
                  : 'bg-indigo-100 text-brand'
              }`}
            >
              <Bell className="w-4 h-4" />
            </div>
            <div>
              <h3
                className={`font-semibold text-sm mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Push Notifications
              </h3>
              <p
                className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Control which browser push notifications you receive
              </p>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Chat Messages
              </p>
              <p
                className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Get notified of new messages in real-time
              </p>
            </div>
            <button
              onClick={() => setChatMessages(!chatMessages)}
              className={`relative inline-flex items-center h-5 w-10 rounded-full transition-colors ${
                chatMessages ? 'bg-brand' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform bg-white rounded-full transition ${
                  chatMessages ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Notification Frequency */}
        <div
          className={`border rounded-lg p-6 shadow-sm space-y-4 ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-200 text-gray-800'
          }`}
        >
          <div className="flex items-start mb-4">
            <div
              className={`p-2 rounded-md mr-3 flex items-center justify-center ${
                isDarkMode
                  ? 'bg-white/10 text-white'
                  : 'bg-brandLight text-brand'
              }`}
            >
              <Clock className="w-4 h-4" />
            </div>
            <div>
              <h3
                className={`font-semibold text-sm mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Notification Frequency
              </h3>
              <p
                className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Control how often you receive notifications
              </p>
            </div>
          </div>
          <div
            className={`space-y-4 text-sm ${
              isDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}
          >
            <div>
              <label
                className={`block text-xs mb-1 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Email Digest Frequency
              </label>
              <select
                value={digestFrequency}
                onChange={(e) => setDigestFrequency(e.target.value)}
                className={`border rounded-md px-3 py-2 w-full focus:outline-none focus:ring focus:border-brand ${
                  isDarkMode
                    ? 'bg-gray-800 text-white border-gray-500'
                    : 'bg-white text-gray-800 border-gray-300'
                }`}
              >
                <option>Instant</option>
                <option>Hourly</option>
                <option>Daily</option>
                <option>Weekly</option>
              </select>
            </div>
            <div className="flex space-x-4">
              {['from', 'to'].map((key, idx) => (
                <div className="flex-1" key={idx}>
                  <label
                    className={`block text-xs mb-1 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    {key === 'from' ? 'Quiet Hours From' : 'To'}
                  </label>
                  <input
                    type="time"
                    value={quietHours[key]}
                    onChange={(e) =>
                      setQuietHours({ ...quietHours, [key]: e.target.value })
                    }
                    className={`border rounded-md px-3 py-2 w-full focus:outline-none focus:ring focus:border-brand ${
                      isDarkMode
                        ? 'bg-gray-800 text-white border-gray-500'
                        : 'bg-white text-gray-800 border-gray-300'
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Document Notifications */}
        <div
          className={`border rounded-lg p-6 shadow-sm space-y-4 ${
            isDarkMode
              ? 'bg-gray-700 border-gray-600 text-white'
              : 'bg-white border-gray-200 text-gray-800'
          }`}
        >
          <div className="flex items-start">
            <div
              className={`p-2 rounded-md mr-3 flex items-center justify-center ${
                isDarkMode
                  ? 'bg-white/10 text-white'
                  : 'bg-indigo-100 text-brand'
              }`}
            >
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <h3
                className={`font-semibold text-sm mb-1 ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Document Notifications
              </h3>
              <p
                className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Control notifications for specific document types
              </p>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <p
                className={`text-sm font-medium ${
                  isDarkMode ? 'text-white' : 'text-gray-800'
                }`}
              >
                Research Documents
              </p>
              <p
                className={`text-xs ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                Receive notifications about research document updates
              </p>
            </div>
            <button
              onClick={() => setResearchDocuments(!researchDocuments)}
              className={`relative inline-flex items-center h-5 w-10 rounded-full transition-colors ${
                researchDocuments ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform bg-white rounded-full transition ${
                  researchDocuments ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Buttons Section */}
      <div className="flex justify-end gap-3 mt-6 px-2">
        <button
          className={`px-4 py-2 rounded-md text-sm border ${
            isDarkMode
              ? 'border-gray-500 text-gray-300 hover:bg-gray-600'
              : 'border-gray-300 text-gray-600 hover:bg-gray-100'
          }`}
        >
          Reset to Default
        </button>
        <button className="px-4 py-2 rounded-md text-sm bg-brand text-white hover:bg-brand">
          Save Changes
        </button>
      </div>
    </>
  );
};

export default NotificationTab;
